import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';

const PrivacyPolicy = () => {
  const theme = useTheme();

  const sections = [
    {
      title: 'Information We Collect',
      content: [
        'Personal Information: We collect personal information such as your name, email address, phone number, and educational background when you register for our services or contact us.',
        'Academic Information: We may collect information about your academic qualifications, test scores, and educational goals to provide personalized counseling services.',
        'Communication Data: We store records of our communications with you, including emails, phone calls, and meeting notes.',
        'Website Usage: We collect information about how you use our website, including pages visited, time spent, and device information.',
      ]
    },
    {
      title: 'How We Use Your Information',
      content: [
        'Service Provision: We use your information to provide study abroad counseling, visa processing, and related educational services.',
        'Communication: We use your contact information to communicate with you about our services, updates, and important information.',
        'Personalization: We use your academic and personal information to tailor our services to your specific needs and goals.',
        'Legal Compliance: We may use your information to comply with legal obligations and regulatory requirements.',
      ]
    },
    {
      title: 'Information Sharing',
      content: [
        'Educational Institutions: We may share your academic information with universities and colleges as part of the application process.',
        'Government Agencies: We may share information with immigration authorities and other government agencies as required for visa processing.',
        'Service Providers: We may share information with third-party service providers who assist us in delivering our services.',
        'Legal Requirements: We may disclose information when required by law or to protect our rights and interests.',
      ]
    },
    {
      title: 'Data Security',
      content: [
        'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
        'We use secure servers and encryption technologies to safeguard your data.',
        'Access to your personal information is restricted to authorized personnel only.',
        'We regularly review and update our security practices to ensure the highest level of protection.',
      ]
    },
    {
      title: 'Your Rights',
      content: [
        'Access: You have the right to request access to the personal information we hold about you.',
        'Correction: You can request correction of any inaccurate or incomplete information.',
        'Deletion: You may request deletion of your personal information, subject to legal and contractual obligations.',
        'Portability: You have the right to receive your data in a structured, machine-readable format.',
        'Withdrawal of Consent: You can withdraw your consent for data processing at any time.',
      ]
    },
    {
      title: 'Cookies and Tracking',
      content: [
        'We use cookies and similar technologies to enhance your browsing experience and analyze website usage.',
        'You can control cookie settings through your browser preferences.',
        'Some features of our website may not function properly if cookies are disabled.',
        'We use analytics tools to understand user behavior and improve our services.',
      ]
    },
    {
      title: 'Third-Party Links',
      content: [
        'Our website may contain links to third-party websites and services.',
        'We are not responsible for the privacy practices of these external sites.',
        'We encourage you to review the privacy policies of any third-party sites you visit.',
        'Your use of third-party services is subject to their respective terms and conditions.',
      ]
    },
    {
      title: 'Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.',
        'We will notify you of any material changes through email or website notification.',
        'Your continued use of our services after changes are posted constitutes acceptance of the updated policy.',
        'We recommend reviewing this policy periodically to stay informed about how we protect your information.',
      ]
    },
    {
      title: 'Contact Information',
      content: [
        'If you have any questions about this Privacy Policy or our data practices, please contact us:',
        'Email: privacy@futureforwards.in',
        'Phone: +91 7356475308, +91 7736715551',
        'Address: Trivandrum, Kerala, India',
        'We will respond to your inquiries within 30 days of receipt.',
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Header />
      
      <Box sx={{ pt: 8, pb: 6 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  color: '#1e3a5f',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#64748b',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#64748b',
                  mt: 2,
                  fontStyle: 'italic',
                }}
              >
                Last updated: December 2024
              </Typography>
            </Box>
          </motion.div>

          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#1e3a5f',
                      mb: 2,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      fontWeight: 700,
                    }}
                  >
                    {section.title}
                  </Typography>
                  {section.content.map((item, itemIndex) => (
                    <Typography
                      key={itemIndex}
                      variant="body1"
                      sx={{
                        color: '#64748b',
                        mb: 2,
                        lineHeight: 1.8,
                        fontSize: '1.1rem',
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            ))}
          </Paper>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
